<template>
  <div class="kpis-catalog" :aria-busy="kpisStore.loading ? 'true' : 'false'">
    <ExLoader v-if="initialLoading" text="Loading KPIs..." />
    <KPIsTab
      v-else
      :kpis="kpisStore.kpis"
      :page="page"
      :perPage="KPI_LIST_PAGE_SIZE"
      :totalCount="kpisStore.totalCount"
      :currencies="kpisStore.currencies"
      :hiddenMenuActions="HIDDEN_MENU_ACTIONS"
      :initialSearchText="search"
      @onSearch="onSearch"
      @checkedItemsChanged="onFiltersChanged"
      @removeFilter="onFilterRemoved"
      @clear-search="onClearSearch"
      @page-change="onPageChange"
      @kpi-created="onKpiCreated"
      @menu-action="onMenuAction"
    />
    <DialogPopup
      v-if="nameChange"
      :dialog="true"
      :type="nameChange.action === 'rename' ? 'rename' : 'text'"
      :ghostText="nameChange.action === 'rename' ? 'Rename' : 'Unarchive and rename'"
      label="KPI"
      description=""
      buttonLabel="Save"
      orgObjectType="kpi"
      maxWidth="596"
      :max_char="50"
      :allowSpecialCharacters="true"
      :isShowCharCounter="true"
      :customClass="true"
      :initialValue="nameChange.kpi.name"
      :apiError="nameChangeError"
      :loading="nameChangeSaving"
      @dialogItem="saveNameChange"
      @clearApiError="nameChangeError = ''"
      @back="closeNameChange"
    />
  </div>
</template>

<script setup>
import { ref, reactive, getCurrentInstance, onMounted } from "vue";
import KPIsTab from "@components/KPIsTab/KPIsTab.vue";
import DialogPopup from "@components/Modal/DialogPopup.vue";
import ExLoader from "@components/common/ExLoader.vue";
import { useKpisStore } from "@/store/pinia/kpi";
import {
  KPI_LIST_PAGE_SIZE,
  buildKpiListParams,
  createDefaultKpiFilters,
  extractKpiErrorMessage,
  parseKpiFilterItems,
  removeKpiFilterChip,
} from "@/utils/kpiCatalogUtils";

const { proxy } = getCurrentInstance();
const kpisStore = useKpisStore();

// Activate, Delete and Clone belong to other tickets, so their menu entries are not offered here.
const HIDDEN_MENU_ACTIONS = ["activate", "delete", "clone"];
const DIRECT_ACTIONS = ["retire", "reactivate", "archive", "unarchive"];
const NAME_CHANGE_ACTIONS = ["rename", "unarchive-and-rename"];
const SUCCESS_VERBS = {
  rename: "renamed",
  retire: "retired",
  reactivate: "reactivated",
  archive: "archived",
  unarchive: "unarchived",
  "unarchive-and-rename": "unarchived and renamed",
};

const page = ref(1);
const search = ref("");
const filters = reactive(createDefaultKpiFilters());
const initialLoading = ref(true);
const nameChange = ref(null);
const nameChangeError = ref("");
const nameChangeSaving = ref(false);
let loadSequence = 0;

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function notifySuccess(message) {
  proxy?.$notifications?.success?.(message);
}

function notifyError(message) {
  proxy?.$notifications?.error?.(message);
}

async function loadKpis() {
  const sequence = ++loadSequence;
  const params = buildKpiListParams({
    page: page.value,
    search: search.value,
    filters,
    currencies: kpisStore.currencies,
  });
  try {
    await kpisStore.fetchKpis(params);
    if (sequence === loadSequence && page.value > 1 && kpisStore.kpis.length === 0) {
      page.value -= 1;
      await loadKpis();
    }
  } catch (error) {
    if (sequence === loadSequence) notifyError(extractKpiErrorMessage(error));
  } finally {
    if (sequence === loadSequence) initialLoading.value = false;
  }
}

function applyFilters(next) {
  Object.assign(filters, next);
  page.value = 1;
  return loadKpis();
}

function onSearch(text) {
  search.value = text;
  page.value = 1;
  return loadKpis();
}

function onFiltersChanged(items) {
  return applyFilters(parseKpiFilterItems(items));
}

function onFilterRemoved(chip) {
  return applyFilters(removeKpiFilterChip(filters, chip));
}

function onClearSearch() {
  search.value = "";
  return applyFilters(createDefaultKpiFilters());
}

function onPageChange(nextPage) {
  page.value = nextPage;
  return loadKpis();
}

async function onKpiCreated() {
  notifySuccess("KPI created as a draft");
  await loadKpis();
}

async function runAction(action, kpi, newName) {
  try {
    await kpisStore.runLifecycleAction({ action, id: kpi.id, newName });
  } catch (error) {
    return extractKpiErrorMessage(error);
  }
  notifySuccess(`KPI "${kpi.name}" ${SUCCESS_VERBS[action]}`);
  await loadKpis();
  return "";
}

async function onMenuAction({ action, kpi }) {
  if (NAME_CHANGE_ACTIONS.includes(action)) {
    nameChangeError.value = "";
    nameChange.value = { action, kpi };
  } else if (DIRECT_ACTIONS.includes(action)) {
    const failure = await runAction(action, kpi);
    if (failure) notifyError(failure);
  }
}

async function saveNameChange(name) {
  const { action, kpi } = nameChange.value;
  nameChangeSaving.value = true;
  const failure = await runAction(action, kpi, String(name).trim());
  nameChangeSaving.value = false;
  if (failure) {
    nameChangeError.value = failure;
  } else {
    closeNameChange();
  }
}

function closeNameChange() {
  nameChange.value = null;
  nameChangeError.value = "";
}

onMounted(() => {
  const accountId = getAccountId();
  if (accountId) kpisStore.fetchOperatingCurrencies(accountId);
  return loadKpis();
});
</script>

<style scoped>
.kpis-catalog {
  width: 100%;
}
</style>
